<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const store = useStore();
const { t } = useI18n();

const [showStageDropdown, toggleDropdown] = useToggle(false);
const isLoading = ref(false);
const isUpdatingStage = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const conversationStages = computed(
  () => store.getters.getConversationStages || []
);

const orderedConversationStages = computed(() => {
  return [...conversationStages.value].sort((stageA, stageB) => {
    const positionA = stageA.position || 0;
    const positionB = stageB.position || 0;

    if (positionA !== positionB) {
      return positionA - positionB;
    }

    return stageA.id - stageB.id;
  });
});

const selectedStage = computed(() => {
  const stageId = currentChat.value?.conversation_stage_id;

  if (stageId) {
    const matchedStage = conversationStages.value.find(
      stage => stage.id === stageId
    );
    if (matchedStage) {
      return matchedStage;
    }
  }

  return currentChat.value?.conversation_stage || null;
});

const hasStageOptions = computed(() => conversationStages.value.length > 0);

const shouldShowStageAction = computed(() => {
  return hasStageOptions.value || !!selectedStage.value;
});

const currentStageIndex = computed(() => {
  const selectedStageId = selectedStage.value?.id;
  if (!selectedStageId) {
    return -1;
  }

  return orderedConversationStages.value.findIndex(
    stage => stage.id === selectedStageId
  );
});

const nextStage = computed(() => {
  if (!orderedConversationStages.value.length) {
    return null;
  }

  if (currentStageIndex.value < 0) {
    return orderedConversationStages.value[0] || null;
  }

  return orderedConversationStages.value[currentStageIndex.value + 1] || null;
});

const canAdvanceStage = computed(() => {
  return !!nextStage.value && !isLoading.value && !isUpdatingStage.value;
});

const stageButtonLabel = computed(() => {
  if (!selectedStage.value) {
    return t('CONVERSATION.STAGE.CHANGE_STAGE.SELECT_PLACEHOLDER');
  }

  return `${selectedStage.value.emoji || ''} ${selectedStage.value.name}`.trim();
});

const stageMenuSections = computed(() => {
  return [
    {
      title: t('CONVERSATION.STAGE.TITLE'),
      items: conversationStages.value.map(stage => ({
        action: 'change_stage',
        value: stage.id,
        label: stage.name,
        emoji: stage.emoji,
        isSelected: stage.id === selectedStage.value?.id,
      })),
      emptyState: t('CONVERSATION.STAGE.CHANGE_STAGE.NO_RESULTS'),
    },
  ];
});

const fetchStagesIfNeeded = async () => {
  if (conversationStages.value.length) {
    return;
  }

  isLoading.value = true;
  await store.dispatch('fetchConversationStages');
  isLoading.value = false;
};

const changeConversationStage = async stage => {
  if (!stage || !currentChat.value?.id) {
    return;
  }

  if (stage.id === currentChat.value.conversation_stage_id) {
    return;
  }

  isUpdatingStage.value = true;

  try {
    const success = await store.dispatch('assignConversationStage', {
      conversationId: currentChat.value.id,
      conversationStageId: stage.id,
      conversationStage: stage,
    });

    if (success) {
      useAlert(
        t('CONVERSATION.STAGE.CHANGE_STAGE.SUCCESSFUL', {
          stage: stage.name,
        })
      );
      return;
    }

    useAlert(t('CONVERSATION.STAGE.CHANGE_STAGE.FAILED'));
  } finally {
    isUpdatingStage.value = false;
  }
};

const handleStageAction = async ({ value }) => {
  toggleDropdown(false);

  const stage = orderedConversationStages.value.find(item => item.id === value);
  await changeConversationStage(stage);
};

const advanceToNextStage = async () => {
  toggleDropdown(false);

  if (!nextStage.value) {
    return;
  }

  await changeConversationStage(nextStage.value);
};

onMounted(() => {
  fetchStagesIfNeeded();
});
</script>

<template>
  <div
    v-show="shouldShowStageAction"
    v-on-clickaway="() => toggleDropdown(false)"
    class="relative flex items-center gap-1"
  >
    <ButtonV4
      v-tooltip="$t('CONVERSATION.STAGE.TITLE')"
      size="sm"
      variant="outline"
      color="slate"
      class="max-w-[11.5rem] !px-2"
      :is-loading="isLoading"
      :disabled="!hasStageOptions || isUpdatingStage"
      @click="toggleDropdown()"
    >
      <span class="flex items-center gap-2 min-w-0 w-full">
        <span
          class="i-lucide-workflow size-3.5 text-n-slate-11 flex-shrink-0"
        />
        <span class="truncate">{{ stageButtonLabel }}</span>
        <span
          class="flex-shrink-0 size-3.5 text-n-slate-11"
          :class="
            showStageDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
      </span>
    </ButtonV4>
    <DropdownMenu
      v-if="showStageDropdown"
      :menu-sections="stageMenuSections"
      show-search
      :search-placeholder="
        $t('CONVERSATION.STAGE.CHANGE_STAGE.INPUT_PLACEHOLDER')
      "
      class="mt-1 ltr:left-0 rtl:right-0 top-full min-w-[14rem]"
      @action="handleStageAction"
    />
    <ButtonV4
      v-tooltip="$t('CONVERSATION.STAGE.NEXT_STAGE_TOOLTIP')"
      size="sm"
      variant="outline"
      color="slate"
      icon="i-lucide-chevrons-right"
      class="!px-2"
      :is-loading="isUpdatingStage"
      :disabled="!canAdvanceStage"
      :aria-label="$t('CONVERSATION.STAGE.NEXT_STAGE')"
      @click="advanceToNextStage"
    />
  </div>
</template>
